import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterSignup = () => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const { language } = useLanguage();

    const texts = {
        no: {
            title: 'Hold deg oppdatert',
            subtitle: 'Meld deg på vårt nyhetsbrev for eksklusive tilbud, mineralvann-tips og nyheter fra Raw Minerals.',
            emailPlaceholder: 'Din e-postadresse',
            namePlaceholder: 'Navn (valgfritt)',
            button: 'Meld deg på',
            success: 'Takk for påmeldingen! Du vil snart motta vårt nyhetsbrev.',
            alreadySubscribed: 'Du er allerede påmeldt nyhetsbrevet.',
            error: 'Noe gikk galt. Prøv igjen.',
            invalidEmail: 'Vennligst oppgi en gyldig e-postadresse.'
        },
        en: {
            title: 'Stay Updated',
            subtitle: 'Subscribe to our newsletter for exclusive offers, mineral water tips and news from Raw Minerals.',
            emailPlaceholder: 'Your email address',
            namePlaceholder: 'Name (optional)',
            button: 'Subscribe',
            success: 'Thank you for subscribing! You will soon receive our newsletter.',
            alreadySubscribed: 'You are already subscribed to our newsletter.',
            error: 'Something went wrong. Please try again.',
            invalidEmail: 'Please enter a valid email address.'
        }
    };

    const t = texts[language] || texts.no;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError(t.invalidEmail);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, name: name || null }),
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
                setEmail('');
                setName('');
            } else {
                setError(t.error);
            }
        } catch (err) {
            setError(t.error);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
            >
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-lg text-white">{t.success}</p>
            </motion.div>
        );
    }

    return (
        <div className="max-w-xl mx-auto text-center">
            <Mail className="w-10 h-10 text-white/60 mx-auto mb-4" />
            <h3 className="font-display text-2xl md:text-3xl font-semibold text-white mb-3">
                {t.title}
            </h3>
            <p className="text-white/70 mb-8 text-sm md:text-base">
                {t.subtitle}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                        type="email"
                        placeholder={t.emailPlaceholder}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none h-12"
                        required
                        data-testid="newsletter-email"
                    />
                    <Input
                        type="text"
                        placeholder={t.namePlaceholder}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="flex-1 sm:max-w-[180px] bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none h-12"
                        data-testid="newsletter-name"
                    />
                </div>
                
                <Button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto bg-white text-stone-900 hover:bg-white/90 rounded-none px-10 h-12 text-xs uppercase tracking-[0.15em] font-semibold"
                    data-testid="newsletter-submit"
                >
                    {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        t.button
                    )}
                </Button>
            </form>

            {error && (
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-400 mt-4 text-sm"
                >
                    {error}
                </motion.p>
            )}
        </div>
    );
};

export default NewsletterSignup;
